import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})

export class FusionChartsService {

  /**Order of color palette of fusion chart */
  GRAPH_COLOR_PALETTE = ['#95E1D3', '#FFE3C4', '#FFA8A8', '#B9CFF9', '#F7EAA3', '#A3A3F7', 'A4FF94'];
  LOCATION_COLOR_PALETTE = ['#df67f1', '#FAF150'];
  /**Color combination of Gender diversity graph */
  GENDER_COLOR_PALETTE = {
    MEN: '#B9CFF9',
    WOMEN: '#FFD6F3'
  }

  /**Color used to 2d column graph */
  COLUMN_GRAPH_COLOR ='#95E1D3';

  /**Common attributes used by all fusion charts */
  FUSION_COMMON_ATTR = {
    theme: "fusion",

    //Number Formatting
    decimals: "0",
    formatNumberScale: "0",
    thousandSeparatorPosition: "2,3",

    //Font style
    labelFont: "Poppins",
    legendItemFont: "Poppins",
    labelFontColor: '#484b59',
    labelFontSize: '12',

    useEllipsesWhenOverflow: "1",
    enableSmartLabels: "1",
    manageLabelOverflow: "1",
  }

  /**Fusion chart attributes for Doughnut chart */
  DOUGHNUT_ATTR = {
    paletteColors: this.GRAPH_COLOR_PALETTE,

    showLabels: '0',
    showValues: '0',

    startingAngle: "90",
    use3DLighting: "0",
    animateClockwise: "1",

    legendPosition: "bottom",
    legendAllowDrag: "0",
    legendNumColumns: 2,
    legendItemFontSize: "12",
    legendiconborderpadding: "5",
    legendItemFontColor: '#484b59',
    legendItemFont: 'Poppins',
    pieRadius: 90,
    chartBottomMargin: 20
  }

  /**Fusion chart attributes for bar graph */
  BAR_GRAPH_ATTR = {
    paletteColors: this.GRAPH_COLOR_PALETTE,
    showLabels: '1',
    showValues: '1',
    maxBarHeight: '16',
    showZeroPlaneValue: '0',
    plotHoverEffect: 0,
  }

  /**Fusion chart attributes for column graph */
  COLUMN_GRAPH_ATTR = {
    paletteColors: this.COLUMN_GRAPH_COLOR,
    showLabels: '1',
    showValues: '1',
    plotHoverEffect: 0,
  }

   raceColors: { [key: string]: string } = {
  asian: "#95E1D3",
  nh_white: "#FFE3C4",
  hispanic: "#FFA8A8",
  nh_black: "#B9CFF9",
  other: "#F7EAA3"
};

  constructor() { }

  /**
   * Ethnicity fusion chart
   * @description data must be in format { label : key  &  value: doc_count}
   * @param data Ethnicity data from aggreagate
   * @param totalResultCount total result count to calculate percentage
   * @returns
   */
  ethnicityChart(data: { key: string, doc_count: number }[], totalResultCount: number) {
    let chartData = [];
    let total = 0;
    for (let i = 0; i < 5 && i < data.length; i++) {
      total += data[i].doc_count
    }

    for (let i = 0; i < 5 && i < data.length; i++) {
      chartData.push({
        label: this.formatName(data[i].key),
        value: this.calculatePercentage(total, data[i].doc_count),
        tooltext: `$label: ${this.calculatePercentage(total, data[i].doc_count)}%`,
        color: this.raceColors[data[i].key]

      })
    }

    const ethnicityChart = {
      "type": "doughnut2d",
      "dataFormat": "json",
      "chart": {
        ...this.FUSION_COMMON_ATTR,
        ...this.DOUGHNUT_ATTR,
      },
      data: chartData
    }
    return ethnicityChart;
  }

  /**
   * Nationality fusion chart
   * @param data Nationality data from aggregate
   * @param totalResultCount total result count to calculate percentage
   *  @description data must be in particular format { label : key  &  value: doc_count}
   * @returns
   */
  nationalityChart(data: { key: string, doc_count: number }[], totalResultCount: number) {
    let chartData = [];
    for (let i = 0; i < 5 && i < data.length; i++) {
      chartData.push({
        label: this.formatName(data[i].key),
        value: this.calculatePercentage(totalResultCount, data[i].doc_count),
        tooltext: `$label: $dataValue`
      })
    }

    const nationalityChart = {
      "type": "bar2d",
      "dataFormat": "json",
      "chart": {
        ...this.FUSION_COMMON_ATTR,
        ...this.BAR_GRAPH_ATTR,
        showXAxisLine: "0",
        showYAxisLine: "1",
        yAxisLineColor: '#d9d9d9',
        numberSuffix: '%',
        showPercentValues: "0",
      },
      "data": chartData
    }
    return nationalityChart;
  }

  /**
 * Gender diversity fusion chart, called from must be present in case of personsearch insighsts.
 * @param data Gender diversity data from aggregate.
 * @param calledFrom Module where chart has been used.
 * @description data must be in particular format { label : key  &  value: doc_count}
 * @returns
 */
  genderDiversityChart(data, calledFrom = '') {
    const CharForDiversity = [];
    data && data.forEach(x => {
      let dataObj = {
        label: `${x.key == 'male' ? 'Men' : 'Women'}`,
        value: x.doc_count,
        tooltext: `$label <br> Talent Count: $dataValue`,
        color: `${x.key == 'male' ? this.GENDER_COLOR_PALETTE.MEN : this.GENDER_COLOR_PALETTE.WOMEN}`,
        ...(calledFrom == 'personSearchInsights' && { link: `${x.label}  ${x.value}` }),
      }
      CharForDiversity.push(dataObj)
    })

    const genderChart = {
      "type": "doughnut2d",
      "dataFormat": "json",
      "chart": {
        ...this.FUSION_COMMON_ATTR,
        ...this.DOUGHNUT_ATTR,
        pieRadius: 90
      },
      "data": CharForDiversity
    }
    return genderChart;
  }

  /**
   * Education fusion chart
   * @param data
   * @description data must be in particular format { label : key  &  value: doc_count}
   * @returns
   */
  educationChart(data: { key: string, doc_count: number }[]) {
    const ChartForExpInCurrentRole = [];
    data && data.forEach(x => {
      let dataObj = {
        label: this.formatName(x.key),
        value: x.doc_count,
        tooltext: `$label <br> Talent Count: $dataValue`
      }
      ChartForExpInCurrentRole.push(dataObj)
    });

    const education = {
      type: "column2d",
      dataFormat: "json",
      chart: {
        "renderAt": "chart-container",
        ...this.FUSION_COMMON_ATTR,
        ...this.COLUMN_GRAPH_ATTR,
        yAxisNameFontColor: '#2C2C2C',
      },
      "data": ChartForExpInCurrentRole
    }
    return education;

  }

  /**
   * Field of study chart
   * @param data
   * @description data must be in particular format { label : key  &  value: doc_count}
   * @returns
   */
  fieldOfStudyChart(data: { key: string, doc_count: number }[]) {
    const CharForStudy = [];
    data = data.slice(0, Math.min(7, data.length));
    data && data.forEach((x, i) => {
      let dataObj = {
        label: this.formatName(x.key),
        value: x.doc_count,
        tooltext: `${this.formatName(x.key)} <br> Talent Count: ${x.doc_count}`,
      }
      CharForStudy.push(dataObj)
    })

    const fieldOfStudy = {
      "type": "bar2d",
      "dataFormat": "json",
      "chart": {
        ...this.FUSION_COMMON_ATTR,
        ...this.BAR_GRAPH_ATTR,
        subCaption: "",
        yAxisNameFontColor: '#2C2C2C',
      },
      "data": CharForStudy
    }
    return fieldOfStudy;
  }

  /**
 * Seniority fusion chart, called from must be present in case of personsearch insighsts.
 * @param data Seniority data.
 * @param calledFrom Module where chart has been used.
 * @description data must be in particular format { label : key  &  value: doc_count}
 * @returns
 */
  seniorityChart(data: { key: string, doc_count: number }[], calledFrom = '') {
    const CharForSeniority = [];
    data = data.slice(0, Math.min(7, data.length));
    data && data.forEach(x => {
      let dataObj = {
        label: this.formatName(x.key),
        value: x.doc_count,
        tooltext: `$label <br> Talent Count: $dataValue`,
      }
      CharForSeniority.push(dataObj)
    })
    const seniorityData = {
      dataFormat: "json",
      chart: {
        ...this.FUSION_COMMON_ATTR,
        ...((calledFrom === 'personSearchInsights') ? this.BAR_GRAPH_ATTR : this.DOUGHNUT_ATTR),
        pieRadius: 90,
      },
      data: CharForSeniority,
    };
    (calledFrom === 'personSearchInsights') && (seniorityData['chart']['formatNumberScale'] = '1')

    return seniorityData
  }

  /**
   * Years of exp. fusion chart, calledFrom must be present in case of personsearch insighsts.
   * @param data Seniority data.
   * @param calledFrom Module where chart has been used.
   * @description data must be in particular format { label : key  &  value: doc_count}
   * @returns
   */
  yearsOfExperience(data: { key: string, doc_count: number }[], calledFrom = '') {
    let CharForYearsOfExperience = [];
    data && data.forEach(x => {
      let dataObj = {
        label: `${x.key}`,
        value: x.doc_count,
        tooltext: `Years: $label <br> Talent Count: $dataValue`,
      }
      CharForYearsOfExperience.push(dataObj);
    });

    CharForYearsOfExperience = CharForYearsOfExperience.filter(
      (el) => !!el.value
    )?.slice(0, 40);

    const yearsOfExp = {
      type: "column2d",
      dataFormat: "json",
      chart: {
        ...this.FUSION_COMMON_ATTR,
        paletteColors: this.COLUMN_GRAPH_COLOR,
        captionFontColor: "#F4AB33",
        showPercentValues: "1",
        yAxisNameFontColor: "#F4AB33",
        legendAllowDrag: "1",
        theme: "fusion",
        valuePosition: "right",
        showYAxisValues: "1",
      },
      data: CharForYearsOfExperience,
    }
    yearsOfExp['chart']['labelFontSize'] = '10'
    return yearsOfExp;
  }

  /**
   * Fusion chart for Functions
   * @param data
   * @description data must be in particular format { label : key  &  value: doc_count}
   * @returns
   */
  functionsChart(data: { key: string, doc_count: number }[]) {
    const CharForFunction = [];
    data = data.slice(0, Math.min(7, data.length));
    data && data.forEach(x => {
      let dataObj = {
        label: this.formatName(x.key),
        value: x.doc_count,
        tooltext: `$label <br> Talent Count: $dataValue`
      }
      CharForFunction.push(dataObj)
    })


    const functionsData = {
      "type": "doughnut2d",
      "dataFormat": "json",
      "chart": {
        ...this.FUSION_COMMON_ATTR,
        ...this.DOUGHNUT_ATTR,
        yAxisNameFontColor: '#2C2C2C',
      },
      "data": CharForFunction
    }
    return functionsData;
  }

  /**Used to calculat percentage */
  calculatePercentage(total: number, value: number) {
    return ((value / total) * 100).toFixed(0);
  }

  /**
 * Used to keep first letter capital
 * @param str
 * @returns
 */
  formatName(str: string) {
    str = str?.replace(/_/g, ' ');
    let stringName = str.toLowerCase().split(' ');
    let newStr = [];
    for (let i = 0; i < stringName.length; i++) {
      if (stringName[i] != 'nh')
        newStr.push(stringName[i].charAt(0).toUpperCase() + stringName[i].slice(1));
    }
    return newStr.join(' ');
  }

  /**
   * Used to seperate numbers with comma, with thousand seperator positions 2,3
   * Example: 123456 -> 1,23,456
   * @param num
   * @returns
   */
  formatNumberWithCommas(num: number) {
    if (num / 1000 == 0) return num;
    const numStr = num.toString();
    let formattedNum = '';
    let commaCount = 0;
    let firstInstance = true;
    for (let i = numStr.length - 1; i >= 0; i--) {
      formattedNum = numStr[i] + formattedNum;
      commaCount++;
      if (firstInstance) {
        if (commaCount == 3) {
          formattedNum = ',' + formattedNum;
          commaCount = 0;
          firstInstance = false;
        }
      }
      else if (commaCount === 2 && i !== 0) {
        formattedNum = ',' + formattedNum;
        commaCount = 0;
      }
    }
    return formattedNum;
  }

  /**
   * Nationality fusion chart
   * @param data Nationality data from aggregate
   * @param totalResultCount total result count to calculate percentage
   *  @description data must be in particular format { label : key  &  value: doc_count}
   * @returns
   */
   seniorityCompareChart(data: { key: string, doc_count: number }[], totalResultCount: number) {
    let chartData = [];
    for (let i = 0; i < data.length; i++) {
      chartData.push({
        label: this.formatName(data[i].key),
        value: this.calculatePercentage(totalResultCount, data[i].doc_count),
        tooltext: `$label: $dataValue`
      })
    }

    const seniorityCompareChart = {
      "type": "bar2d",
      "dataFormat": "json",
      "chart": {
        ...this.FUSION_COMMON_ATTR,
        ...this.BAR_GRAPH_ATTR,
        showXAxisLine: "0",
        showYAxisLine: "1",
        yAxisLineColor: '#d9d9d9',
        numberSuffix: '%',
        showPercentValues: "0",
      },
      "data": chartData
    }
    return seniorityCompareChart;
  }
}
